/**
 * 店舗サービス管理: アカウント関連のQuery Hooks
 * TanStack Queryを使用したデータフェッチングとキャッシュ管理
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  getAccountByIdAuthenticated,
  updateAccountSettings,
  getAccountSettingsNextAuthenticated,
  updateAccountSettingsNext,
  getSellersAuthenticated,
  getSellerGroupsAuthenticated,
  getProductGroupTypesAuthenticated,
  getTableRecordCountsAuthenticated,
  getAccountTasksAuthenticated,
  getAccountFeaturesAuthenticated,
  updateAccountFeaturesAuthenticated,
  updateAccountContractAuthenticated,
  getAggregationPeriodsAuthenticated,
  getFirstLevelProductGroupsAuthenticated,
} from '@/lib/api/mise/accounts';
import type {
  UpdateAccountSettingsRequest,
  UpdateAccountSettingsNextRequest,
  UpdateAccountFeaturesRequest,
  UpdateAccountContractRequest,
} from '@/lib/types/mise/account';
import {
  miseAccountKeys,
  miseSellerKeys,
  miseSellerGroupKeys,
  miseProductGroupTypeKeys,
} from '../keys';
import { CACHE_TIME } from '../client';

/**
 * アカウント詳細を取得するQuery Hook（mise用）
 * @param id アカウントID
 */
export function useMiseAccount(id: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseAccountKeys.detail(id),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getAccountByIdAuthenticated(id, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!id, // 認証状態が準備完了し、IDが存在する場合のみ実行
    staleTime: CACHE_TIME.ACCOUNT_DETAIL, // アカウント詳細と同じキャッシュ時間を使用
  });
}

/**
 * アカウント設定を更新するMutation Hook
 * @param accountId アカウントID
 */
export function useUpdateAccountSettings(accountId: string) {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (request: UpdateAccountSettingsRequest) => {
      const authHeaders = getAuthHeader();
      return updateAccountSettings(accountId, request, authHeaders);
    },
    onSuccess: () => {
      // アカウント詳細を再取得
      queryClient.invalidateQueries({ queryKey: miseAccountKeys.detail(accountId) });
      // 商品グループタイプ変更時に第1階層商品グループのキャッシュも更新
      queryClient.invalidateQueries({
        queryKey: miseAccountKeys.firstLevelProductGroups(accountId),
      });
    },
  });
}

/**
 * アカウント設定 next（予約中のバッチ集計条件）を取得するQuery Hook
 * @param accountId アカウントID
 */
export function useMiseAccountSettingsNext(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseAccountKeys.settingsNext(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getAccountSettingsNextAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * アカウント設定 next を更新するMutation Hook
 *
 * 保存成功時は settingsNext と detail を両方 invalidate する。
 * swap 自体は翌日バッチで行われるため、detail の current 値は保存直後には変わらない。
 * @param accountId アカウントID
 */
export function useUpdateMiseAccountSettingsNext(accountId: string) {
  const queryClient = useQueryClient();
  const { getAuthHeader } = useAuthHeader();

  return useMutation({
    mutationFn: async (request: UpdateAccountSettingsNextRequest) => {
      const authHeaders = getAuthHeader();
      return updateAccountSettingsNext(accountId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: miseAccountKeys.settingsNext(accountId) });
      queryClient.invalidateQueries({ queryKey: miseAccountKeys.detail(accountId) });
    },
  });
}

/**
 * 店舗一覧を取得するQuery Hook（snapshot_hot.sellers）
 * @param accountId アカウントID
 */
export function useMiseSellers(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseSellerKeys.list(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSellersAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * 店舗グループ一覧を取得するQuery Hook（snapshot_hot.seller_groups）
 * @param accountId アカウントID
 */
export function useMiseSellerGroups(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseSellerGroupKeys.list(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getSellerGroupsAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * 商品グループタイプ一覧を取得するQuery Hook（snapshot_hot.product_group_types）
 * @param accountId アカウントID
 */
export function useMiseProductGroupTypes(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseProductGroupTypeKeys.list(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getProductGroupTypesAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * 集計期間の選択肢を取得するQuery Hook
 */
export function useMiseAggregationPeriods(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseAccountKeys.aggregationPeriods(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getAggregationPeriodsAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * 第1階層の商品グループ一覧を取得するQuery Hook
 * @param accountId アカウントID
 */
export function useMiseFirstLevelProductGroups(accountId: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseAccountKeys.firstLevelProductGroups(accountId),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getFirstLevelProductGroupsAuthenticated(accountId, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!accountId,
    staleTime: CACHE_TIME.ACCOUNTS_LIST,
  });
}

/**
 * テーブルレコード件数を取得するQuery Hook
 * @param id アカウントID
 * @param enabled Popover表示時のみデータ取得するための制御フラグ
 */
export function useMiseTableRecordCounts(id: string, enabled: boolean) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseAccountKeys.tableRecordCounts(id),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getTableRecordCountsAuthenticated(id, authHeaders);
    },
    enabled: enabled && !authHeaderLoading && isAuthorized && !!id,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * アカウントのバッチタスク一覧を取得するQuery Hook
 * @param id アカウントID
 */
export function useMiseAccountTasks(id: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseAccountKeys.tasks(id),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getAccountTasksAuthenticated(id, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!id,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * アカウント機能設定を取得するQuery Hook
 */
export function useMiseAccountFeatures(id: string) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: miseAccountKeys.features(id),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getAccountFeaturesAuthenticated(id, authHeaders);
    },
    enabled: !authHeaderLoading && isAuthorized && !!id,
    staleTime: CACHE_TIME.ACCOUNT_DETAIL,
  });
}

/**
 * アカウント機能設定を更新するMutation Hook
 */
export function useUpdateAccountFeatures(accountId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateAccountFeaturesRequest) => {
      const authHeaders = getAuthHeader();
      return updateAccountFeaturesAuthenticated(accountId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: miseAccountKeys.features(accountId) });
    },
  });
}

/**
 * アカウント契約状態を更新するMutation Hook
 */
export function useUpdateAccountContract(accountId: string) {
  const { getAuthHeader } = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateAccountContractRequest) => {
      const authHeaders = getAuthHeader();
      return updateAccountContractAuthenticated(accountId, request, authHeaders);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: miseAccountKeys.detail(accountId) });
    },
  });
}
